import React, { useState, useEffect } from 'react';
import { RadioGroup, FormControlLabel, Radio, Box, styled, TextField } from '@mui/material';
import { Question } from '../../types';
import { getTestId, getOptionTestId } from '../../utils/testUtils';

const StyledRadio = styled(Radio)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.5)',
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiTypography-root': {
    color: 'text.primary',
    fontSize: '1rem',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(4),
  width: 'calc(100% - 32px)',
  '& .MuiInputBase-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    borderColor: theme.palette.primary.main,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
}));

interface Props {
  question: Question;
  selectedOption?: number;
  onChange: (optionId: number) => void;
  customOtherValue?: string;
  onCustomOtherChange?: (value: string) => void;
}

export const MultipleChoiceQuestion: React.FC<Props> = ({ 
  question, 
  selectedOption, 
  onChange,
  customOtherValue = '',
  onCustomOtherChange = () => {}
}) => {
  const [hasOtherOption, setHasOtherOption] = useState(false);
  const [otherOptionId, setOtherOptionId] = useState<number | null>(null);

  useEffect(() => {
    // Check if any option has is_other flag
    const otherOption = question.options?.find(opt => opt.is_other);
    setHasOtherOption(!!otherOption);
    setOtherOptionId(otherOption?.id || null);
  }, [question.options]);

  // Find the 'Other' option if it exists
  const otherOption = question.options?.find(option => option.is_other);
  
  // Check if the 'Other' option is selected
  const isOtherSelected = otherOption && selectedOption === otherOption.id;

  return (
    <Box sx={{ width: '100%' }} {...getTestId(`question-multiple-choice-${question.id}`)}>
      <RadioGroup
        {...getTestId('multiple-choice-options')}
        value={selectedOption || ''}
        onChange={(e) => {
          const optionId = parseInt(e.target.value);
          onChange(optionId);
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(question.options ?? []).map((option) => (
            <React.Fragment key={option.id}>
              <StyledFormControlLabel
                {...getOptionTestId(option.id)}
                value={option.id}
                control={<StyledRadio {...getTestId(`radio-option-${option.id}`)} />}
                label={option.front_text}
              />
              {option.is_other && selectedOption === option.id && (
                <StyledTextField
                  {...getTestId('other-option-input')}
                  label="Please specify"
                  variant="outlined"
                  size="small"
                  value={customOtherValue}
                  onChange={(e) => onCustomOtherChange(e.target.value)}
                  fullWidth
                  placeholder="Enter your custom response"
                  required
                  autoFocus
                  error={customOtherValue.trim() === ''}
                  helperText={customOtherValue.trim() === '' ? '' : ''}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </RadioGroup>
    </Box>
  );
};